import { BoundingBox, SpatialDatabase } from '../util';
import { GateResolver } from '../path-finding';
import { NavigationMeshPathFinder } from './NavigationMeshPathFinder';
import { PathNode } from './PathNode';

/**
 * Bundles the three pieces of navigation state that always travel together:
 *
 * - `database`   — spatial index (octree) for "find nodes near position X"
 *                  and ray picking.
 * - `pathFinder` — A* + RegionGraph + GateResolver hookup for routing.
 * - `gateResolver` — conditional traversal predicates (doors etc.).
 *
 * Plus the flat node list (used for debug visualization and bulk operations).
 * A single `setPathNodes()` call seeds every internal index, so callers can
 * never forget one of the steps.
 *
 * Note: the active `VolumeGrid` lives on `Scene.volumeGrid`, not here. The
 * navigation subsystem doesn't read it — floor heights are resolved at
 * runtime by `NavigationAwareObject.onStep` querying the scene's grid
 * directly, and the optimizer ignores Y entirely.
 */
export class NavigationSystem {
  public readonly database: SpatialDatabase<PathNode>;
  public readonly pathFinder: NavigationMeshPathFinder;
  public readonly gateResolver: GateResolver;
  public readonly maxNeighbourStepHeight: number;

  private nodes: PathNode[] = [];

  constructor(boundingBox: BoundingBox, voxelSize: number = 1, maxNeighbourStepHeight: number = Infinity) {
    this.database = new SpatialDatabase<PathNode>(boundingBox);
    this.gateResolver = new GateResolver();
    this.pathFinder = new NavigationMeshPathFinder(this.gateResolver, voxelSize);
    this.maxNeighbourStepHeight = maxNeighbourStepHeight;
  }

  /**
   * Seed (or re-seed) the system with a list of path nodes. Populates the
   * spatial database and rebuilds the region graph used by the path finder.
   */
  public setPathNodes(nodes: PathNode[]): void {
    this.nodes = nodes;
    this.database.addArray(nodes);
    this.pathFinder.setPathNodes(nodes);
  }

  /**
   * Replace the current node set entirely (used by the tiled streamer when tiles
   * load or evict). Resets the spatial database in-place so registered picking
   * callbacks keep their reference, then rebuilds the region graph.
   */
  public rebuildWith(nodes: PathNode[]): void {
    this.database.clear();
    this.nodes = nodes;
    this.database.addArray(nodes);
    this.pathFinder.setPathNodes(nodes);
  }

  /**
   * Add nodes to the spatial index only. Called by the tiled streamer when a
   * new tile's nodes are ready so the octree stays current incrementally rather
   * than being rebuilt from scratch on every tile change.
   */
  public addNodes(nodes: PathNode[]): void {
    this.database.addArray(nodes);
  }

  /**
   * Remove nodes from the spatial index only. Called by the tiled streamer
   * before evicting a tile so the octree stays current without a full rebuild.
   */
  public removeNodes(nodes: PathNode[]): void {
    this.database.removeArray(nodes);
  }

  /**
   * Update the pathfinder node list and rebuild the region graph without
   * touching the spatial index. Used by the tiled streamer after re-stitching:
   * the octree is already up-to-date via addNodes/removeNodes, so only the
   * pathfinder (region graph) needs to be refreshed.
   */
  public syncPathFinder(allNodes: PathNode[]): void {
    this.nodes = allNodes;
    this.pathFinder.setPathNodes(allNodes);
  }

  /** All path nodes currently in the system. Read-only — do not mutate. */
  public getAllNodes(): readonly PathNode[] {
    return this.nodes;
  }
}
